import React, { useEffect, useState } from "react";
import { Settings as SettingsIcon, Plus, Trash2, Save, Image as ImageIcon, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { canFinancial } from "@/lib/permissions";
import { fmtDate, fmtMoney } from "@/lib/format";
import { PageHeader, Loading, Field, SectionCard } from "@/components/common";
import { DataTable, Btn, TextInput, Select, Textarea } from "@/components/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Modal } from "@/components/Modal";

export default function Settings() {
  return (
    <div>
      <PageHeader title="Settings" subtitle="Business units, service library, capacity rules and public holidays" icon={SettingsIcon} />
      <Tabs defaultValue="bu">
        <TabsList className="mb-4">
          <TabsTrigger value="bu" data-testid="tab-bu">Business Units</TabsTrigger>
          <TabsTrigger value="services" data-testid="tab-services">Service Types</TabsTrigger>
          <TabsTrigger value="capacity" data-testid="tab-capacity">Capacity Rules</TabsTrigger>
          <TabsTrigger value="automation" data-testid="tab-automation">Automation</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="tab-holidays">Public Holidays</TabsTrigger>
        </TabsList>
        <TabsContent value="bu"><BusinessUnits /></TabsContent>
        <TabsContent value="services"><ServiceTypes /></TabsContent>
        <TabsContent value="capacity"><Capacity /></TabsContent>
        <TabsContent value="automation"><Automation /></TabsContent>
        <TabsContent value="branding"><Branding /></TabsContent>
        <TabsContent value="holidays"><Holidays /></TabsContent>
      </Tabs>
    </div>
  );
}

function BusinessUnits() {
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", description: "", colour: "#4CAF3F" });
  const load = () => api.get("/business-units").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.post("/business-units", form); toast.success("Saved"); setModal(false); setForm({ name: "", code: "", description: "", colour: "#4CAF3F" }); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <div className="flex justify-end mb-3"><Btn onClick={() => setModal(true)} data-testid="new-bu-btn"><Plus size={16} /> Business Unit</Btn></div>
      <div className="grid sm:grid-cols-3 gap-4">
        {rows.map((b) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: b.colour }} /><span className="font-medium text-slate-800">{b.name}</span></div>
            <div className="text-xs text-slate-400 mt-1">{b.code}</div>
            <p className="text-sm text-slate-500 mt-2">{b.description}</p>
          </div>
        ))}
      </div>
      <Modal open={modal} onOpenChange={setModal} title="New business unit" footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-bu-btn">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Code"><TextInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          <Field label="Colour"><TextInput type="color" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} className="h-10 p-1" /></Field>
          <Field label="Description" className="col-span-2"><TextInput value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}

function ServiceTypes() {
  const { user } = useAuth();
  const fin = canFinancial(user);
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", default_duration_mins: 120, default_charge: 0, default_frequency: "Monthly", description: "" });
  const load = () => api.get("/service-types").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { if (!form.name) return toast.error("Name required"); try { await api.post("/service-types", form); toast.success("Saved"); setModal(false); load(); } catch (e) { toast.error(apiError(e)); } };
  const columns = [
    { key: "name", label: "Service", className: "font-medium text-slate-800" },
    { key: "category", label: "Category" },
    { key: "default_duration_mins", label: "Duration", render: (r) => `${r.default_duration_mins} min` },
    { key: "default_frequency", label: "Default frequency" },
    ...(fin ? [{ key: "default_charge", label: "Default charge", align: "right", render: (r) => fmtMoney(r.default_charge) }] : []),
  ];
  return (
    <div>
      <div className="flex justify-end mb-3"><Btn onClick={() => setModal(true)} data-testid="new-service-btn"><Plus size={16} /> Service Type</Btn></div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden"><DataTable columns={columns} rows={rows} /></div>
      <Modal open={modal} onOpenChange={setModal} title="New service type" footer={<><Btn variant="secondary" onClick={() => setModal(false)}>Cancel</Btn><Btn onClick={save} data-testid="save-service-btn">Save</Btn></>}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Name" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Category"><TextInput value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
          <Field label="Duration (min)"><TextInput type="number" value={form.default_duration_mins} onChange={(e) => setForm({ ...form, default_duration_mins: +e.target.value })} /></Field>
          <Field label="Default frequency"><Select value={form.default_frequency} onChange={(e) => setForm({ ...form, default_frequency: e.target.value })}>{["One-off", "Weekly", "Fortnightly", "Monthly", "Quarterly", "Six Monthly", "Annual"].map((f) => <option key={f}>{f}</option>)}</Select></Field>
          {fin && <Field label="Default charge (€)"><TextInput type="number" value={form.default_charge} onChange={(e) => setForm({ ...form, default_charge: +e.target.value })} /></Field>}
          <Field label="Description" className="col-span-2"><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
        </div>
      </Modal>
    </div>
  );
}

function Capacity() {
  const [form, setForm] = useState(null);
  useEffect(() => { api.get("/settings/capacity").then((r) => setForm(r.data)); }, []);
  const save = async () => { try { await api.put("/settings/capacity", form); toast.success("Capacity rules saved"); } catch (e) { toast.error(apiError(e)); } };
  if (!form) return <Loading />;
  const f = (k, label, type = "number") => (
    <Field label={label}><TextInput type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: type === "number" ? +e.target.value : e.target.value })} /></Field>
  );
  return (
    <SectionCard title="Global capacity & scheduling rules">
      <div className="grid sm:grid-cols-3 gap-4">
        {f("max_jobs_per_day", "Max jobs per day")}
        {f("max_hours_per_day", "Max hours per day")}
        {f("min_break_mins", "Min break (mins)")}
        {f("max_travel_mins_per_day", "Max travel per day (mins)")}
        {f("earliest_start", "Earliest start", "time")}
        {f("latest_finish", "Latest finish", "time")}
        {f("session_timeout_mins", "Session timeout (mins)")}
        {f("lockout_threshold", "Lockout after N failed logins")}
      </div>
      <div className="flex justify-end mt-4"><Btn onClick={save} data-testid="save-capacity-btn"><Save size={16} /> Save rules</Btn></div>
    </SectionCard>
  );
}

function Automation() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/settings/reminders").then((r) => setS(r.data));
  useEffect(() => { load(); }, []);
  const save = async (next) => { try { const { data } = await api.put("/settings/reminders", next); setS(data); toast.success("Automation settings saved"); } catch (e) { toast.error(apiError(e)); } };
  const runNow = async () => { setBusy(true); try { const { data } = await api.post("/reminders/run"); if (data.skipped === "disabled") { toast.info("Reminders are turned off — enable them to send appointment reminders."); } else { toast.success(`Reminder run complete — ${data.sent} email(s) queued for ${data.target_date}`); } load(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  if (!s) return <Loading />;
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <SectionCard title="Automated appointment reminders">
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <span className="text-sm text-slate-700">Send reminders automatically</span>
            <input type="checkbox" checked={!!s.enabled} onChange={(e) => save({ enabled: e.target.checked, days_before: s.days_before })} data-testid="reminders-enabled" className="rounded h-5 w-5" />
          </label>
          <Field label="Days before visit">
            <Select value={s.days_before} onChange={(e) => save({ enabled: s.enabled, days_before: +e.target.value })} data-testid="reminders-days">
              {[1, 2, 3, 7].map((d) => <option key={d} value={d}>{d} day{d > 1 ? "s" : ""} before</option>)}
            </Select>
          </Field>
          <Btn onClick={runNow} disabled={busy} data-testid="run-reminders-btn"><Save size={15} /> Run reminders now</Btn>
          <p className="text-xs text-slate-400">Runs hourly in the background. Emails are logged (mock) until a provider key is configured.</p>
        </div>
      </SectionCard>
      <SectionCard title="Status">
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between"><dt className="text-slate-400">Enabled</dt><dd className="text-slate-700">{s.enabled ? "Yes" : "No"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-400">Lead time</dt><dd className="text-slate-700">{s.days_before} day(s)</dd></div>
          <div className="flex justify-between"><dt className="text-slate-400">Last run</dt><dd className="text-slate-700">{s.last_run ? new Date(s.last_run).toLocaleString("en-IE") : "—"}</dd></div>
          <div className="flex justify-between"><dt className="text-slate-400">Last sent</dt><dd className="text-slate-700">{s.last_sent ?? 0} email(s)</dd></div>
        </dl>
      </SectionCard>
    </div>
  );
}

function Holidays() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", date: "", region: "Ireland" });
  const load = () => api.get("/public-holidays").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => { if (!form.name || !form.date) return toast.error("Name and date required"); try { await api.post("/public-holidays", form); toast.success("Added"); setForm({ name: "", date: "", region: "Ireland" }); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (h) => { await api.delete(`/public-holidays/${h.id}`); load(); };
  return (
    <div className="grid md:grid-cols-3 gap-5">
      <SectionCard title="Add holiday">
        <div className="space-y-3">
          <Field label="Name"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Date"><TextInput type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Region"><TextInput value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} /></Field>
          <Btn onClick={save} className="w-full" data-testid="save-holiday-btn"><Plus size={16} /> Add</Btn>
        </div>
      </SectionCard>
      <SectionCard title="Configured public holidays" className="md:col-span-2" noPad>
        <DataTable rows={rows} columns={[
          { key: "name", label: "Holiday", className: "font-medium text-slate-800" },
          { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
          { key: "region", label: "Region" },
          { key: "actions", label: "", align: "right", render: (r) => <button onClick={() => del(r)} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 size={15} /></button> },
        ]} />
      </SectionCard>
    </div>
  );
}


function Branding() {
  const { refresh } = useAuth();
  const [form, setForm] = useState({ name: "", logo_url: "", brand_color: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("seq_token") || "";
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { api.get("/settings/branding").then((r) => setForm(r.data)).catch(() => {}); }, []);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd);
      setForm((f) => ({ ...f, logo_url: data.url }));
      toast.success("Logo uploaded — remember to save");
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/branding", form);
      toast.success("Branding saved");
      await refresh();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const logoSrc = form.logo_url ? `${backend}${form.logo_url}?auth=${token}` : null;

  return (
    <SectionCard title={<span className="flex items-center gap-2"><ImageIcon size={16} /> Account branding</span>}>
      <p className="text-sm text-slate-500 mb-5">Your account name and logo appear in the app header for everyone in this account.</p>
      <div className="max-w-md space-y-5">
        <Field label="Account name">
          <TextInput value={form.name} data-testid="branding-name" onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your organisation name" />
        </Field>
        <div>
          <div className="text-sm font-medium text-slate-600 mb-2">Logo</div>
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-xl border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
              {logoSrc ? <img src={logoSrc} alt="Logo" className="h-full w-full object-contain" data-testid="branding-logo-preview" /> : <ImageIcon size={22} className="text-slate-300" />}
            </div>
            <div className="flex items-center gap-2">
              <label data-testid="branding-logo-input" className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-slate-300 rounded-lg text-sm text-slate-500 cursor-pointer hover:border-seq-green">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {uploading ? "Uploading…" : "Upload logo"}
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} disabled={uploading} />
              </label>
              {form.logo_url && <button onClick={() => setForm({ ...form, logo_url: "" })} className="text-sm text-slate-400 hover:text-red-500" data-testid="branding-logo-remove">Remove</button>}
            </div>
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-slate-600 mb-2">Brand accent colour</div>
          <div className="flex items-center gap-3">
            <input type="color" value={form.brand_color || "#4CAF3F"} data-testid="branding-color"
              onChange={(e) => setForm({ ...form, brand_color: e.target.value })}
              className="h-10 w-14 rounded-lg border border-slate-200 cursor-pointer bg-white p-1" />
            <TextInput value={form.brand_color || ""} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} placeholder="#4CAF3F" className="w-32" data-testid="branding-color-hex" />
            {form.brand_color && <button onClick={() => setForm({ ...form, brand_color: "" })} className="text-sm text-slate-400 hover:text-red-500">Reset</button>}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Used for the header accent bar and account badge.</p>
        </div>
        <Btn onClick={save} disabled={saving} data-testid="save-branding-btn">{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save branding</Btn>
      </div>
    </SectionCard>
  );
}
